import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Button, Card, Divider, SxProps, Typography } from '@mui/material';
import { SvgImage } from 'components/images/SvgImage';
import { getBlueprintCalculations } from 'features/blueprints/utils';
import { Blueprint } from 'types';
import { SolutionItemData, buildEquipmentItem } from './utils';

interface ProposedSolutionBoxProps {
  blueprintDetails?: Blueprint;
  readOnly?: boolean;
  sx?: SxProps;
  titleSx?: SxProps;
  compact?: boolean;
}

export const ProposedSolutionBox = ({
  blueprintDetails,
  readOnly = false,
  sx = {},
  titleSx = {},
  compact = false,
}: ProposedSolutionBoxProps) => {
  const { t } = useTranslation();

  const { hybrid, conventional, hasGrid, hasKite, mainsConnectionMaxAmps, mainsConnectionMaxKw } =
    getBlueprintCalculations(blueprintDetails);
  const powerSetup = hybrid?.powerSetup;

  const generatorTypeId = powerSetup?.generatorTypeId;
  const batteryTypeId = powerSetup?.batteryTypeId;

  const generatorEquipment = powerSetup?.generator;
  const batteryEquipment = powerSetup?.battery;

  // Build solution items
  const items: SolutionItemData[] = useMemo(() => {
    // Battery (left)
    const batteryManualDescriptions: string[] = [];
    if (powerSetup?.batterySizeKwh) {
      batteryManualDescriptions.push(`${powerSetup.batterySizeKwh} kWh`);
    }
    if (powerSetup?.batteryConverterPowerKw) {
      batteryManualDescriptions.push(`${powerSetup.batteryConverterPowerKw} kW`);
    }

    const batteryItem = buildEquipmentItem({
      equipmentTypeId: batteryTypeId,
      equipment: batteryEquipment,
      placeholderImage: '/static/images/greener-box-forest.svg',
      title: t('blueprints:details.proposedSolution.battery.title'),
      manualDescriptions: batteryManualDescriptions,
      count: powerSetup?.batteryCount,
    });

    // Generator (center)
    const generatorManualDescriptions = powerSetup?.generatorCapacityKva
      ? [`${powerSetup.generatorCapacityKva} kVA`]
      : [];

    const generatorItem = buildEquipmentItem({
      equipmentTypeId: generatorTypeId,
      equipment: generatorEquipment,
      placeholderImage: '/static/images/generator-forest.svg',
      title: t('blueprints:details.proposedSolution.generator.title'),
      manualDescriptions: generatorManualDescriptions,
      count: powerSetup?.generatorCount,
    });

    // Monitoring & Control (right) - always placeholder
    const monitoringItem: SolutionItemData = {
      imagePath: '/static/images/data-forest.svg',
      title: t('blueprints:details.proposedSolution.monitoring.title'),
      descriptions: t('blueprints:details.proposedSolution.monitoring.description').split('\n'),
    };

    return [batteryItem, ...(powerSetup?.generatorCount ? [generatorItem] : []), monitoringItem];
  }, [generatorTypeId, generatorEquipment, batteryTypeId, batteryEquipment, powerSetup, t]);

  const alignments = ['flex-start', 'center', 'flex-end'];
  const gridValue = mainsConnectionMaxAmps
    ? `${mainsConnectionMaxAmps} A`
    : mainsConnectionMaxKw
      ? `${mainsConnectionMaxKw} kW`
      : '';
  const proposedSolutionSubtitle = [
    hasGrid
      ? t('blueprints:details.proposedSolution.subtitle.hasGrid', { value: gridValue })
      : t('blueprints:details.proposedSolution.subtitle.hasNoGrid'),
    ...(hasKite ? [t('blueprints:details.proposedSolution.subtitle.hasKite')] : []),
  ].join(', ');

  const DeviceDescription = ({
    item,
    index,
    shouldShowCount,
  }: {
    item: SolutionItemData;
    index: number;
    shouldShowCount: boolean;
  }) => {
    const textStyles = {
      textAlign: compact ? 'left' : 'center',
      pl: compact ? 1 : 0,
      width: { xs: '172px', sm: '140px', md: '172px' },
    };

    return (
      <Box display="flex" flexDirection="column">
        {item.title && (
          <Typography
            variant="h4"
            sx={{
              mb: 1,
              ...textStyles,
            }}
          >
            {shouldShowCount ? `${item.count} x ${item.title}` : item.title}
          </Typography>
        )}

        {item.descriptions.map((description: string, descIndex: number) => (
          <Typography
            data-testid={`solution-item-description-${index}-${descIndex}`}
            key={descIndex}
            variant="body2"
            color={item.isError && descIndex === item.descriptions.length - 1 ? 'warning.main' : 'text.primary'}
            sx={{
              ...textStyles,
              ...(item.isError && descIndex === item.descriptions.length - 1
                ? {
                    fontWeight: 500,
                  }
                : {}),
            }}
          >
            {description}
          </Typography>
        ))}
      </Box>
    );
  };

  return (
    <Card data-testid="proposed-solution" sx={{ padding: 4, borderRadius: '8px', ...sx }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box sx={titleSx}>
          <Typography variant="h3">{t('blueprints:details.proposedSolution.title')}</Typography>
          <Typography variant="caption">{proposedSolutionSubtitle}</Typography>
        </Box>
        {!readOnly && (
          <Button
            data-testid="edit-proposed-solution-button"
            component={Link}
            to={`/solutions/${blueprintDetails?.blueprintId}/edit?step=3`}
          >
            {t('blueprints:details.proposedSolution.editButton')}
          </Button>
        )}
      </Box>

      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-evenly"
        alignItems={{ xs: 'center', sm: 'flex-start' }}
        gap={3}
      >
        {items.map((item, index) => {
          // Show count badge and count in title only for battery/generator items with count > 1
          // Monitoring system (index 2) should never show count
          const isMonitoringItem = index === 2;
          const hasMultipleItems = (item.count ?? 0) > 1;
          const shouldShowCount = hasMultipleItems && !isMonitoringItem;
          const imageSrc = item.imageUrl ?? item.imagePath;

          return (
            <Box
              key={index}
              display="flex"
              flexDirection={compact ? 'row' : 'column'}
              alignItems={{
                xs: 'center',
                sm: compact ? 'center' : alignments[index],
              }}
            >
              <Box
                sx={{
                  width: { xs: '172px', sm: '140px', md: '172px' },
                  height: { xs: '150px', sm: '120px', md: '150px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  overflow: 'visible',
                  position: 'relative',
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[300] : 'transparent'),
                  borderRadius: 2,
                  p: 1,
                }}
              >
                {imageSrc?.includes('.svg') && imageSrc?.startsWith('http') ? (
                  <SvgImage logoUrl={imageSrc} width="100%" height="100%" />
                ) : (
                  <img
                    data-testid={`equipment-image-${index}`}
                    src={imageSrc}
                    alt={item.title || 'Equipment'}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                )}

                {shouldShowCount && (
                  <Box
                    data-testid={`equipment-count-badge-${index}`}
                    sx={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      width: '30px',
                      height: '30px',
                      background: (theme) => theme.palette.primary.main,
                      border: '1px solid white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'white',
                        fontWeight: 900,
                      }}
                    >
                      {item.count}
                    </Typography>
                  </Box>
                )}
              </Box>

              <DeviceDescription item={item} index={index} shouldShowCount={shouldShowCount} />
            </Box>
          );
        })}
      </Box>

      <Divider sx={{ my: 2.5 }} />

      <Box sx={titleSx}>
        <Typography variant="h3">{t('blueprints:details.proposedSolution.conventionalSetup.title')}</Typography>
        <Typography variant="caption">
          {t('blueprints:details.proposedSolution.conventionalSetup.description', {
            amount: conventional?.powerSetup.generatorCount,
            kva: conventional?.powerSetup.generatorCapacityKva,
          })}
        </Typography>
      </Box>
    </Card>
  );
};
